// @flow

import { useEffect, useState } from 'react';
import { authPkgConfig } from '@auravisionlabs/aura-pkg-auth-service';
import {
  getAuthPkgUser,
  initAuthPkgUser,
} from '../../services/auth-pkg-user';

export type TagPermissionsT = {
  loading: boolean,
  canManageTagsButton: boolean,
  canCreateTag: boolean,
  canUpdateTag: boolean,
  canDeleteTag: boolean,
  canBulkAssignTag: boolean,
  canBulkRemoveTag: boolean,
  canAssignTagToLocation: boolean,
  canRemoveTagFromLocation: boolean,
};

const DENY: TagPermissionsT = {
  loading: false,
  canManageTagsButton: false,
  canCreateTag: false,
  canUpdateTag: false,
  canDeleteTag: false,
  canBulkAssignTag: false,
  canBulkRemoveTag: false,
  canAssignTagToLocation: false,
  canRemoveTagFromLocation: false,
};

export const useTagPermissions = (): TagPermissionsT => {
  const [perms, setPerms] = useState<TagPermissionsT>({
    ...DENY,
    loading: true,
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        let authUser = getAuthPkgUser();
        if (!authUser) {
          authUser = await initAuthPkgUser();
        }
        if (!authUser) {
          if (mounted) setPerms({ ...DENY, loading: false });
          return;
        }
        const orgId = await authUser.getPrimaryOrgId();
        const R = authPkgConfig.resources;
        const A = authPkgConfig.authActions;

        const [
          canCreateTag,
          canUpdateTag,
          canDeleteTagBase,
          canCreateLocTag,
          canDeleteLocTag,
        ] = await Promise.all([
          authUser.can(R.tags, A.create, { orgIds: [orgId] }).evaluate(),
          authUser.can(R.tags, A.update, { orgIds: [orgId] }).evaluate(),
          authUser.can(R.tags, A.delete, { orgIds: [orgId] }).evaluate(),
          authUser
            .can(R.locationTags, A.create, { orgIds: [orgId] })
            .evaluate(),
          authUser
            .can(R.locationTags, A.delete, { orgIds: [orgId] })
            .evaluate(),
        ]);

        if (!mounted) return;
        setPerms({
          loading: false,
          canManageTagsButton: !!(canCreateTag || canDeleteTagBase),
          canCreateTag: !!canCreateTag,
          canUpdateTag: !!canUpdateTag,
          canDeleteTag: !!(canDeleteTagBase && canDeleteLocTag),
          canBulkAssignTag: !!canCreateLocTag,
          canBulkRemoveTag: !!canDeleteLocTag,
          canAssignTagToLocation: !!canCreateLocTag,
          canRemoveTagFromLocation: !!canDeleteLocTag,
        });
      } catch (e) {
        if (mounted) setPerms({ ...DENY, loading: false });
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return perms;
};
